/**
 * ApprovalCard — what a manager sees when a cashier asks them to approve a
 * return: who, which sale, items, reason, how much goes back and how. The
 * manager confirms with their own PIN, or declines with an optional note.
 * Used by the pop-up prompt and by /approvals/:id (email / notification link).
 */
import { useState } from "react";
import { formatDistanceToNow } from "date-fns";
import { CheckCircle2, Clock, KeyRound, Loader2, XCircle } from "lucide-react";
import { toast } from "sonner";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { useMoneyFormat } from "@/components/common/Money";
import approvalService, { type ApprovalRequest } from "@/services/approvalService";
import { getApiErrorMessage } from "@/utils/api-error";

const METHOD_LABEL: Record<string, string> = { cash: "cash", momo: "mobile money", bank: "bank" };

export default function ApprovalCard({ request, onDone }: { request: ApprovalRequest; onDone?: () => void }) {
  const fmt = useMoneyFormat();
  const qc = useQueryClient();
  const [pin, setPin] = useState("");
  const [declining, setDeclining] = useState(false);
  const [note, setNote] = useState("");
  const s = request.summary;

  const refresh = () => {
    qc.invalidateQueries({ queryKey: ["approvals"] });
    qc.invalidateQueries({ queryKey: ["returns"] });
  };

  const approve = useMutation({
    mutationFn: () => approvalService.approve(request._id, pin),
    onSuccess: () => {
      toast.success(
        request.kind === "credit_sale"
          ? `Approved — ${request.requestedByName}'s sale is recorded`
          : `Approved — ${request.requestedByName} can give the refund now`,
      );
      setPin("");
      refresh();
      onDone?.();
    },
    onError: (e) => {
      setPin("");
      toast.error(getApiErrorMessage(e, "Couldn't approve"));
      refresh();
    },
  });
  const reject = useMutation({
    mutationFn: () => approvalService.reject(request._id, note.trim() || undefined),
    onSuccess: () => {
      toast(request.kind === "credit_sale" ? "Credit declined" : "Return declined");
      refresh();
      onDone?.();
    },
    onError: (e) => toast.error(getApiErrorMessage(e, "Couldn't decline")),
  });

  const open = request.status === "pending";
  const busy = approve.isPending || reject.isPending;

  return (
    <div className="space-y-4">
      {request.kind === "credit_sale" ? (
        <div className="rounded-xl border bg-muted/30 p-4 text-sm">
          <p>
            <strong>{request.requestedByName}</strong> wants to sell to <strong>{s.customerName}</strong> on credit.
          </p>
          <ul className="mt-3 space-y-1">
            {s.lines.map((l, i) => (
              <li key={i} className="flex justify-between gap-3">
                <span>{l.quantity} × {l.name}</span>
                <span className="tabular-nums">{fmt.minor(l.amountMinor)}</span>
              </li>
            ))}
          </ul>
          <dl className="mt-3 space-y-1 border-t pt-3">
            <div className="flex justify-between"><dt className="text-muted-foreground">Sale total</dt><dd className="tabular-nums">{fmt.minor(s.totalMinor)}</dd></div>
            <div className="flex justify-between"><dt className="text-muted-foreground">Paying now</dt><dd className="tabular-nums">{fmt.minor(s.paidMinor ?? 0)}</dd></div>
            <div className="flex justify-between"><dt className="text-muted-foreground">Already owes</dt><dd className="tabular-nums">{fmt.minor(s.outstandingMinor ?? 0)}</dd></div>
            <div className="flex justify-between"><dt className="text-muted-foreground">Credit limit</dt><dd className="tabular-nums">{fmt.minor(s.limitMinor ?? 0)}</dd></div>
          </dl>
          <div className="mt-3 flex items-baseline justify-between border-t pt-3">
            <span className="font-medium">Will owe after this sale</span>
            <span className="text-xl font-semibold tabular-nums">{fmt.minor(s.wouldOweMinor ?? 0)}</span>
          </div>
          <p className="mt-2 flex items-center gap-1 text-xs text-muted-foreground">
            <Clock className="h-3 w-3" aria-hidden /> Asked {formatDistanceToNow(new Date(request.createdAt), { addSuffix: true })}
          </p>
        </div>
      ) : (
      <div className="rounded-xl border bg-muted/30 p-4 text-sm">
        <p>
          <strong>{request.requestedByName}</strong> wants to return items from sale{" "}
          <strong>{s.invoiceNumber}</strong>
          {s.customerName ? ` (${s.customerName})` : ""}.
        </p>
        <ul className="mt-3 space-y-1">
          {s.lines.map((l, i) => (
            <li key={i} className="flex justify-between gap-3">
              <span>
                {l.quantity} × {l.name}
                <span className="text-muted-foreground">{l.disposition === "write_off" ? " · write off" : l.disposition ? " · back on shelf" : ""}</span>
              </span>
              <span className="tabular-nums">{fmt.minor(l.amountMinor)}</span>
            </li>
          ))}
        </ul>
        <p className="mt-3 text-muted-foreground">
          Reason: {s.reasonLabel}
          {s.note ? ` — "${s.note}"` : ""}
        </p>
        <div className="mt-3 flex items-baseline justify-between border-t pt-3">
          <span className="font-medium">
            {s.refundMinor > 0 ? `Refund by ${METHOD_LABEL[s.refundMethod || ""] || "—"}` : "Taken off what they owe"}
          </span>
          <span className="text-xl font-semibold tabular-nums">{fmt.minor(s.refundMinor > 0 ? s.refundMinor : s.totalMinor)}</span>
        </div>
        {s.refundMinor > 0 && s.arOffsetMinor > 0 && (
          <p className="mt-1 text-xs text-muted-foreground">{fmt.minor(s.arOffsetMinor)} also comes off what the customer owes.</p>
        )}
        <p className="mt-2 flex items-center gap-1 text-xs text-muted-foreground">
          <Clock className="h-3 w-3" aria-hidden /> Asked {formatDistanceToNow(new Date(request.createdAt), { addSuffix: true })}
        </p>
      </div>
      )}

      {!open ? (
        <div className="flex items-center gap-2 rounded-lg border p-3 text-sm">
          {request.status === "approved" ? (
            <CheckCircle2 className="h-4 w-4 text-[#4CAF50]" aria-hidden />
          ) : (
            <XCircle className="h-4 w-4 text-muted-foreground" aria-hidden />
          )}
          <span>
            {request.status === "approved" && `Approved by ${request.decidedByName}.`}
            {request.status === "rejected" && `Declined by ${request.decidedByName}${request.decisionNote ? `: ${request.decisionNote}` : "."}`}
            {request.status === "cancelled" && "The cashier withdrew this request."}
            {request.status === "expired" && "This request expired — the cashier can send a new one."}
            {request.status === "failed" && `Couldn't be recorded: ${request.failureReason}`}
            {request.status === "processing" && "Being recorded…"}
          </span>
        </div>
      ) : declining ? (
        <div className="space-y-2">
          <Input value={note} onChange={(e) => setNote(e.target.value)} placeholder="Why? (optional) — the cashier will see this" maxLength={300} />
          <div className="flex gap-2">
            <Button variant="outline" onClick={() => setDeclining(false)} disabled={busy}>
              Back
            </Button>
            <Button variant="destructive" onClick={() => reject.mutate()} disabled={busy}>
              {reject.isPending && <Loader2 className="mr-2 h-4 w-4 animate-spin" aria-hidden />}
              {request.kind === "credit_sale" ? "Decline credit" : "Decline return"}
            </Button>
          </div>
        </div>
      ) : (
        <form
          className="space-y-2"
          onSubmit={(e) => {
            e.preventDefault();
            if (/^\d{4,6}$/.test(pin)) approve.mutate();
          }}
        >
          <label htmlFor={`pin-${request._id}`} className="flex items-center gap-2 text-sm font-medium">
            <KeyRound className="h-4 w-4" aria-hidden /> Your manager PIN
          </label>
          <div className="flex flex-wrap gap-2">
            <Input
              id={`pin-${request._id}`}
              type="password"
              inputMode="numeric"
              autoComplete="off"
              maxLength={6}
              value={pin}
              onChange={(e) => setPin(e.target.value.replace(/\D/g, "").slice(0, 6))}
              placeholder="••••"
              className="h-11 w-[150px] text-center text-lg tracking-[0.4em]"
            />
            <Button type="submit" disabled={busy || !/^\d{4,6}$/.test(pin)} className="h-11 bg-[#4CAF50] hover:bg-[#45A049] text-white">
              {approve.isPending && <Loader2 className="mr-2 h-4 w-4 animate-spin" aria-hidden />}
              Approve
            </Button>
            <Button type="button" variant="outline" className="h-11" onClick={() => setDeclining(true)} disabled={busy}>
              Decline
            </Button>
          </div>
        </form>
      )}
    </div>
  );
}
